import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	inject,
	input,
	Input,
	OnDestroy,
	OnInit,
	output,
	Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
	ValidationErrors,
} from '@angular/forms';
import { CompanyHideEvent, FormBuilderService } from './form-builder.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { Observable, Subject, takeUntil } from 'rxjs';
import { AppConfig } from './form-schema-api.service';
import { _ } from '@ngx-translate/core';
import { MatIconRegistry, IconResolver, MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';

export interface FormSchemaStatus {
	status: boolean;
	errors: any[];
	formData: Record<string, string> | undefined;
	extraItems: Record<string, any>[] | undefined;
	setService?: any;
}

@Component({
	selector: 'lib-form-builder',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, FormsModule, NgxSpinnerModule, MatIconModule],
	templateUrl: './form-builder.component.html',
	styleUrl: './form-builder.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormBuilderComponent implements OnInit, OnDestroy {
	/** The id of the form for the form schema */
	@Input() formId!: string;
	/** Data the form should be pre-populated with */
	@Input() formData: Record<string, any> | undefined;
	/** whether to show the form submit button */
	@Input() showSubmit = false;
	/** Observable that emits when the form should be re-rendered. Eg if form type (and schema id) changes */
	@Input() formUpdate: Observable<string> | undefined;

	@Input() appConfig!: AppConfig;

	@Input() companyId!: string;

	/**
	 * Anchors the "please tick all the boxes" warning directly under the
	 * last required checkbox on the page instead of at the bottom of the
	 * form. Use when optional fields (e.g. a survey-consent box) appear
	 * after the checkbox group — without this, the warning drifts down
	 * past them and looks like it belongs to the wrong field.
	 */
	@Input() inlineRequiredCheckboxWarning = false;

	/**
	 * Message shown in the "Important" warning box when required checkboxes
	 * on the page are unticked. Override per app/journey when a council
	 * needs custom wording (e.g. GRPO-98 Gloucestershire Notices).
	 */
	@Input() requiredCheckboxWarningMessage =
		'If you cannot tick all of the boxes above, please contact us to discuss your documents further.';

	/** Emits the form once the sumbit button is pressed */
	@Output() formSubmit = new EventEmitter<Record<string, string>>();
	/** Form changes emitted on change */
	@Output() formStatus = new EventEmitter<FormSchemaStatus>();

	@Output() schema = new EventEmitter<Record<string, any>>();

	@Output() companySet = new EventEmitter<string>();
	@Output() companyHide = new EventEmitter<CompanyHideEvent>();
	@Output() serviceSet = new EventEmitter<string>();
	@Output() resourceSet = new EventEmitter<string>();
	@Output() staffSet = new EventEmitter<string>();
	@Output() addDuration = new EventEmitter<string>();
	@Output() formReady = new EventEmitter<Record<string, any>>();
	@Output() updateDatastore = new EventEmitter<Record<string, any>>();
	@Output() addressSet = new EventEmitter<any>();
	@Output() awsLookupSet = new EventEmitter<any>();
	@Output() extraItemSet = new EventEmitter<Record<string, any>>();
	@Output() extraItemChanged = new EventEmitter<Record<string, any>>();
	@Output() venueSet = new EventEmitter<any>();
	@Output() processing = new EventEmitter<boolean>();


	formBuilder = inject(FormBuilderService);
	spinner = inject(NgxSpinnerService);
	cdr = inject(ChangeDetectorRef);
	iconRegistry: MatIconRegistry = inject(MatIconRegistry);
	router = inject(Router);

	destroy$$ = new Subject<void>();
	formFields: Record<string, any>[] = [];
	form!: FormGroup<any>;
	formType = "";
	today = new Date().toISOString().split('T')[0];
	hasUntickedRequiredCheckboxes = false;
	lastVisibleRequiredCheckboxField: Record<string, any> | null = null;
	/** `required_checkbox_warning` from the form schema, if the form sets it. */
	schemaRequiredCheckboxWarning: boolean | string | undefined;
	/** `show_required_asterisk` from the form schema; off unless the schema sets it true. */
	schemaShowRequiredAsterisk = false;

	debounceTimer: any;
	searchText = '';
	suggestions: any[] = [];

	async ngOnInit(): Promise<void> {
		this.spinner.show();
		console.log('Registering FormBuilderComponent', this.companyId);

		this.formBuilder.companySet$.subscribe((companySetValue) => {
			console.log(`Company set value received in component: ${companySetValue}`);
			this.companySet.emit(companySetValue);
		});

		this.formBuilder.companyHide$.subscribe((companyHideEvent) => {
			console.log('Company hide event received in component:', companyHideEvent);
			this.companyHide.emit(companyHideEvent);
		});

		this.formBuilder.serviceSet$.subscribe((serviceSetValue) => {
			console.log(`Service set value received in component: ${serviceSetValue}`);
			this.serviceSet.emit(serviceSetValue);
		});

		this.formBuilder.resourceSet$.subscribe((resourceSetValue) => {
			console.log(`Resource set value received in component: ${resourceSetValue}`);
			this.resourceSet.emit(resourceSetValue);
		});

		this.formBuilder.staffSet$.subscribe((staffSetValue) => {
			console.log(`Staff set value received in component: ${staffSetValue}`);
			this.staffSet.emit(staffSetValue);
		});

		this.formBuilder.addDuration$.subscribe((durationValue) => {
			console.log(`Add duration value received in component: ${durationValue}`);
			this.addDuration.emit(durationValue);
		});

		this.formBuilder.addressSet$.subscribe((address) => {
			console.log(`Address set in component: ${address}`);
			this.addressSet.emit(address);
		});

		this.formBuilder.awsLookupSet$.subscribe((lookupResult) => {
			console.log(`AWS lookup result in component:`, lookupResult);
			this.awsLookupSet.emit(lookupResult);

			// Refresh visibility whenever lookup state changes (success or clear)
			if (this.form && this.formFields?.length) {
				this.refreshVisibility();
			}
		});

		this.formBuilder.foundExtraItem$.subscribe((extraItem) => {
			console.log(`Extra item set in component: `, extraItem);
			this.extraItemSet.emit(extraItem);
		});

		this.formBuilder.changeExtraItem$.subscribe((extraItem) => {
			console.log(`Extra item changed in component: `, extraItem);
			this.extraItemChanged.emit(extraItem);
		});

		// Form ready
		this.formBuilder.formReady$.subscribe((formReady) => {
			console.log(`Form ready received in component: `, formReady);
			this.formReady.emit(formReady);
		});

		this.formBuilder.updateDatastore$.subscribe((update) => {
			console.log(`Update datastore received in component: `, update);
			this.updateDatastore.emit(update);
		});

		this.formBuilder.processing$.subscribe((update) => {
			console.log(`Update processing state in component: `, update);
			this.processing.emit(update);
		});

		if (this.formUpdate) {
			this.formUpdate.pipe(takeUntil(this.destroy$$)).subscribe(async (id) => {
				this.spinner.show();
				this.formId = id;
				this.formFields = [];
				this.populateForm();
			});
		}

		await this.populateForm();

		// Restore any persisted AWS lookup state so fields gated on
		// custom_all_lookups_successful (and the lookupValidated-driven hiding
		// of match-field inputs) behave as if the lookups had just been run.
		// Must run before refreshVisibility so the first visibility pass sees
		// the restored lookupSuccess / lookupValidated flags.
		this.formBuilder.restoreLookupStates(this.formId);

		if (this.form && this.formFields?.length) {
			this.refreshVisibility();
		}

		this.form.valueChanges
			.pipe(takeUntil(this.destroy$$))
			.subscribe((formValues) => {
				this.onFormUpdate(formValues);
				if (this.form && this.formFields?.length) {
					this.refreshVisibility();
				}
			});

		// If formData was provided (e.g. restoring answers when navigating back in the journey),
		// replay onFormUpdate so that all schema behaviours re-trigger: service/department setting,
		// conditional visibility, extra items, etc. This must run after the valueChanges subscription
		// is active so that any further cascading changes are also captured.
		if (this.formData && Object.keys(this.formData as Record<string, any>).length > 0) {
			await this.onFormUpdate(this.form.value);
		}
	}

	private refreshVisibility(): void {
		if (!Array.isArray(this.formFields)) {
			return;
		}

		const formId = this.formId;
		const currentPage = this.router.url.replace('/', '');
		let continueVisible = false;
		this.formFields.forEach(field => {
			if (field['page'] === currentPage) {
				const wasVisible = field['visible'];

				const hasShowCondition =
					field['show'] &&
					Object.keys(field['show']).length > 0;

				const isVisible = hasShowCondition
					? this.formBuilder.evaluateExtendedConditional(formId, field, this.form)
					: true;

				if (!isVisible && wasVisible) {
					const control = this.form.get(field['field']);
					if (control) {
						control.reset(null, { onlySelf: true, emitEvent: false });
						control.markAsPristine({ onlySelf: true, emitEvent: false });
						control.markAsUntouched({ onlySelf: true, emitEvent: false });
					}
					// The reset above uses emitEvent false, so onFormUpdate does not
					// run and the stored answer has to be dropped here.
					this.clearStoredAnswer(field['field']);
				}

				field['visible'] = isVisible;

				// If a fee-bearing field is hidden, trigger removal now.
				// setExtraItems is only called from shouldShowConditional (template *ngIf),
				// which stops firing once the element is removed from the DOM — so we must
				// explicitly trigger it here when we know the field is hidden. This runs on
				// every pass, not just the transition, because the component is rebuilt on
				// navigation and would otherwise never remove a fee added on an earlier
				// visit. setExtraItems only emits while it still tracks the fee.
				if (!isVisible && field['extra-item']) {
					void this.formBuilder.setExtraItems(formId, field, this.appConfig);
				}

				// continue button visibility
				if (isVisible && field['continue'] === true) {
					console.log('Continue button visible due to field:', field['field']);
					continueVisible = true;
				}
			}
		});

		// PD-700: only allow Continue when every visible field on this page is
		// valid — required checkboxes that the user unticks must re-hide the
		// button so abandoned bookings can't slip through.
		const visibleFieldsOnPage = this.formFields.filter(
			f => f['page'] === currentPage && f['visible'] === true,
		);
		const allVisibleValid = visibleFieldsOnPage.every(f => {
			const ctrl = this.form.get(f['field']);
			return !ctrl || ctrl.valid;
		});
		this.hasUntickedRequiredCheckboxes = visibleFieldsOnPage.some(f =>
			f['type'] === 'checkbox' &&
			f['required'] &&
			!this.form.get(f['field'])?.value,
		);

		// Anchor point for the "please tick all the boxes" warning. When
		// inlineRequiredCheckboxWarning is on, the template renders the
		// warning right after this field — that keeps it visually attached
		// to the checkbox group instead of drifting below any optional
		// fields that come after it.
		const requiredCheckboxes = visibleFieldsOnPage.filter(f =>
			f['type'] === 'checkbox' && f['required'],
		);
		this.lastVisibleRequiredCheckboxField = requiredCheckboxes.length
			? requiredCheckboxes[requiredCheckboxes.length - 1]
			: null;

		this.pruneHiddenAnswers(currentPage);

		this.showSubmit = continueVisible && allVisibleValid;
		this.cdr.markForCheck();
	}

	/**
	 * Drops the stored answer for any field on another page whose show rules no
	 * longer pass.
	 *
	 * buildForm only creates controls for the current page, so fields belonging
	 * to other pages have none to reset and are cleared through the answer store
	 * instead. Rules are evaluated without side effects, since the field is not
	 * on the page being shown.
	 */
	private pruneHiddenAnswers(currentPage: string): void {
		if (!this.formId) return;

		const store = this.getAnswerStore();
		const answers = store[this.formId];
		if (!answers) return;

		for (const field of this.formFields) {
			const name = field['field'];
			if (field['page'] === currentPage) continue;
			if (!Object.prototype.hasOwnProperty.call(answers, name)) continue;

			const rules = field['show'];
			if (!rules || Object.keys(rules).length === 0) continue;

			const isVisible = this.formBuilder.evaluateExtendedConditional(
				this.formId,
				field,
				this.form,
				false,
			);

			if (!isVisible) {
				delete answers[name];
				field['visible'] = false;
				field['value'] = undefined;
				// Written before the next field is evaluated, because the rules read
				// the store back, so chained fields clear in one pass.
				sessionStorage.setItem('form_builder_answers', JSON.stringify(store));
			}
		}
	}

	/** Removes one field's answer from the stored answers for this form. */
	private clearStoredAnswer(fieldName: string): void {
		if (!this.formId) return;

		const store = this.getAnswerStore();
		if (!store[this.formId] ||
			!Object.prototype.hasOwnProperty.call(store[this.formId], fieldName)) {
			return;
		}

		delete store[this.formId][fieldName];
		sessionStorage.setItem('form_builder_answers', JSON.stringify(store));
	}


	/**
	 * SUP-2485: lets host pages using [showSubmit]="false" enforce required
	 * fields from their own Continue button. Reveals errors on visible fields
	 * and returns whether they're all valid (hidden fields ignored, as in the
	 * internal allVisibleValid gate).
	 */
	validateAndReveal(): boolean {
		if (!this.form || !Array.isArray(this.formFields)) {
			return true;
		}

		this.refreshVisibility();

		const currentPage = this.router.url.replace('/', '');
		const visibleFieldsOnPage = this.formFields.filter(
			f => f['page'] === currentPage && f['visible'] === true,
		);

		visibleFieldsOnPage.forEach(f => {
			this.form.get(f['field'])?.markAsTouched();
		});
		this.cdr.markForCheck();

		return visibleFieldsOnPage.every(f => {
			const ctrl = this.form.get(f['field']);
			return !ctrl || ctrl.valid;
		});
	}

	ngOnDestroy(): void {
		if (this.debounceTimer) {
			clearTimeout(this.debounceTimer);
		}
		this.destroy$$.next();
		this.destroy$$.complete();
	}

	/**
	 * Schema `show_required_asterisk`: whether required labels get the visual
	 * " *". Defaults to off — a client must opt in via the form schema.
	 * Required-ness is always conveyed programmatically via aria-required.
	 */
	get showRequiredAsterisk(): boolean {
		return this.schemaShowRequiredAsterisk;
	}

	get showRequiredCheckboxWarning(): boolean {
		return this.hasUntickedRequiredCheckboxes && this.schemaRequiredCheckboxWarning !== false;
	}

	/** Schema wording wins over the @Input. */
	get checkboxWarningMessage(): string {
		return typeof this.schemaRequiredCheckboxWarning === 'string'
			? this.schemaRequiredCheckboxWarning
			: this.requiredCheckboxWarningMessage;
	}

	async populateForm() {
		console.log(`Populating form for formId: ${this.formId}`);
		this.form = await this.formBuilder.buildForm(this.formId, [], this.appConfig, this.companyId);
		this.formType = this.formBuilder.getType(this.formId);
		this.formFields = this.formBuilder.getFields(this.formId);
		this.schemaRequiredCheckboxWarning = this.formBuilder.getRequiredCheckboxWarning(this.formId);
		this.schemaShowRequiredAsterisk = this.formBuilder.getShowRequiredAsterisk(this.formId);

		this.cdr.markForCheck();

		if (this.formData) {
			for (const key of Object.keys(this.formData)) {
				if (this.form.controls[key]) {
					this.form.controls[key].setValue(this.formData[key]);
				}
			}
		}

		// Pre-populate only cross-page copy_data targets from sessionStorage
		const copyDataTargets = new Set<string>();
		if (this.formFields) {
			for (const f of this.formFields) {
				if (f.copy_data?.to) {
					for (const target of f.copy_data.to) {
						copyDataTargets.add(target);
					}
				}
			}
		}
		if (copyDataTargets.size > 0) {
			const answerStore = this.getAnswerStore();
			const storedAnswers = answerStore[this.formId];
			if (storedAnswers) {
				for (const key of copyDataTargets) {
					const control = this.form.controls[key];
					if (control && !control.value && storedAnswers[key]) {
						control.setValue(storedAnswers[key], { emitEvent: false });
					}
				}
			}
		}

		// console.log(`END Populating form for formId: ${this.formId}`);
		this.spinner.hide();
	}

	async onFormUpdate(formValues: Record<string, string>) {
		const hasErrors = [];
		for (const field of this.formFields) {
			// console.log('Checking field for update:', `|${field['field']}|`, `|${field['page']}|`, `|${this.router.url.replace('/','')}|`);
			if (field['page'] === this.router.url.replace('/', '')) {
				// console.log('GOT HERE', `|${field['page']}|`, `|${this.router.url.replace('/','')}|`);
				const control = this.form.get(field['field']);
				this.formData = formValues;
				console.log(this.formData);

				if (control) {
					if (control instanceof FormControl && field['conditions']) {
						control.updateValueAndValidity({ onlySelf: true });
					}
					if (!control.disabled) {
						field['value'] = control.value;
						this.persistAnswer(field['field'], control.value);
					}
					if (control.errors) {
						hasErrors.push({
							field: field,
							control: control,
						});
					}

					// set serviceSetValue if present
					if ((control as any).serviceSetValue) {
						// console.log(`Field '${field['field']}' has serviceSetValue:`, (control as any).serviceSetValue);
						// const extraItems = await this.formBuilder.setExtraItems(this.formId, field, this.appConfig);
						this.formStatus.emit({
							status: false,
							errors: hasErrors,
							formData: this.formData,
							extraItems: [],
							setService: (control as any).serviceSetValue,
						});
					}
				}

				// console.log('Checking for serviceSetValue on field:', field['field'], control);
				// const extraItems = await this.formBuilder.setExtraItems(this.formId, field, this.appConfig);
				// console.log('Extra items after form update:', extraItems);
				if (hasErrors.length > 0) {
					this.formStatus.emit({
						status: false,
						errors: hasErrors,
						formData: this.formData,
						extraItems: [],
					});
				} else {
					this.formStatus.emit({
						status: true,
						errors: [],
						formData: this.formData,
						extraItems: [],
					});
				}

				if (!this.showSubmit) {
					//console.log('emitting schema on onFo//rmUpdate');
					//this.schema.emit(this.formBuilder.getSchema());
				}
			}
		}

	}


	errorKeys(field: string): string[] {
		const control = this.form.get(field);
		if (control?.errors) {
			console.log('errors for field ', field);
			console.log(Object.keys(control.errors));
			return Object.keys(control.errors);
		}
		return [];
	}

	showErrors(field: string): boolean {
		const control = this.form.get(field);
		return !!(control && control.invalid && (control.dirty || control.touched));
	}

	/**
	 * The question title to render. Strips any asterisk typed into the title in the
	 * form schema, so the only asterisk shown is the one the template adds from
	 * field.required - otherwise a question can look required but not be marked as
	 * required in the code.
	 */
	displayTitle(field: Record<string, any>): string {
		const title = field['rendered_title'] ? field['rendered_title'] : field['title'];
		if (!title) {
			return title;
		}
		return String(title).replace(/\s*\*\s*$/, '').trim();
	}

	/**
	 * Schema-authored content blocks start at h3, which skips h2 under the page h1.
	 * Shift every heading in the content up one level so the outline is unbroken.
	 */
	contentHtml(html: string): string {
		if (!html) {
			return html;
		}
		return html.replace(/<(\/?)h([3-6])\b/gi, (_m, slash, level) => `<${slash}h${Number(level) - 1}`);
	}

	/** ids for aria-describedby: hint and any visible error messages for the field */
	describedBy(field: Record<string, any>): string | null {
		const ids: string[] = [];
		if (field['helptext']) {
			ids.push(`${field['field']}-hint`);
		}
		if (this.showErrors(field['field'])) {
			ids.push(`${field['field']}-error`);
		}
		if (field['lookupError'] || field['lookupUsed'] || field['lookupNameMismatch']) {
			ids.push(`${field['field']}-lookup-error`);
		}
		return ids.length ? ids.join(' ') : null;
	}

	getErrorMessage(errorKey: string, field: string): string {
		const errors = this.form.get(field)?.errors;
		const error = errors?.[errorKey];
		switch (errorKey) {
			case 'required':
				return 'This field is required.';
			case 'minlength':
				return `Minimum length is ${error.requiredLength}.`;
			case 'maxlength':
				return `Maximum length is ${error.requiredLength}.`;
			case 'email':
				return 'Enter a valid email address like name@example.com';
			//pattern is currently only being used for phone number validation so this works for now..
			case 'pattern':
				return 'Enter a valid phone number like 07700900000.';
			default:
				return error.message || 'Invalid input.';
		}
	}

	submit() {
		// SUP-2496: validate visible fields only — hidden conditional fields keep
		// their required validators, so this.form.valid would wrongly block here.
		if (this.validateAndReveal()) {
			this.formSubmit.emit(this.form.value);
			this.schema.emit(this.formBuilder.getSchema());
		} else {
			this.form.markAllAsTouched();
			return;
		}
	}

	keypressOnRadio(event: any) {
		if (event.code === 'Enter' || event.code === 'Space') {
			const evt = new MouseEvent('click', {
				bubbles: true,
				cancelable: true,
				view: window,
			});
			event.srcElement.certificates[0].dispatchEvent(evt);
		}
	}

	getInputType(field: any): string {
		if (
			field.type === 'number' &&
			Array.isArray(field.validation) &&
			field.validation.some((v: string) => ['phone', 'mobile', 'contact'].includes(v))
		) {
			return 'tel';
		}
		return field.type;
	}

	private getAnswerStore(): any {
		const raw = sessionStorage.getItem('form_builder_answers');
		return raw ? JSON.parse(raw) : {};
	}

	private persistAnswer(fieldName: string, value: any): void {
		if (!this.formId) return;

		const store = this.getAnswerStore();

		store[this.formId] ??= {};
		store[this.formId][fieldName] = value;

		sessionStorage.setItem('form_builder_answers', JSON.stringify(store));
	}

	async onSearch(event: KeyboardEvent, field: any): Promise<void> {
		const ignoredKeys = [
			'ArrowUp',
			'ArrowDown',
			'Enter',
			'Escape',
			'Tab',
			'Shift',
			'Control',
			'Alt',
			'Meta',
		];

		const key = event.key;

		// Skip keys that may interrupt accessibility
		if (ignoredKeys.includes(key)) {
			return;
		}

		field.selected_resource = undefined;
		field.searching = true;
		field.noResults = false;

		try {
			clearTimeout(this.debounceTimer);
			this.debounceTimer = setTimeout(async () => {
				this.suggestions = [];
				const query = (event.target as HTMLInputElement).value;
				this.searchText = query.trim();

				if (this.searchText) {
					const results = await this.formBuilder.getVenueSearchResults(
						this.companyId,
						this.searchText,
						this.appConfig
					);
					this.suggestions = Array.isArray(results) ? results : [];

					field.searching = false;

					// Filter by field type if filter_by is specified
					if (field.filter_by) {
						const filterControl = this.form.get(field.filter_by);
						if (filterControl && filterControl.value) {
							this.suggestions = this.suggestions.filter(
								(suggestion) =>
									suggestion.venue_type?.toLowerCase().trim() ===
									filterControl.value.toLowerCase().trim()
							);
							console.log(`Filtering by: ${filterControl.value}`);
							console.log(`Filtered Suggestions:`, this.suggestions);
						}
					}

					if (this.suggestions.length === 0) {
						field.noResults = true;
					}
				} else {
					field.searching = false;
					this.suggestions = [];
				}

				this.cdr.markForCheck();
			}, 500);
		} catch (error) {
			console.error('Search error:', error);
			field.searching = false;
			field.noResults = true;
			this.cdr.markForCheck();
		}
	}

	clearSearch(field: any): void {
		this.searchText = '';
		this.suggestions = [];
		field.selected_resource = null;
		field.searching = false;
		field.noResults = false;
		field.answer = null; // Clear the answer on the field

		const control = this.form.get(field.field);
		if (control) {
			control.setValue('');
			control.updateValueAndValidity();
		}

		// Emit null to clear the datastore in parent app
		this.venueSet.emit({
			venue: null,
			field: field.field
		});

		this.cdr.markForCheck();
	}

	selectSuggestion(suggestion: any, field: any): void {
		this.suggestions = [];
		this.searchText = suggestion.pretty_name || suggestion.venue_name;
		field.selected_resource = suggestion;
		field.answer = this.searchText; // Store answer on field for template display

		const control = this.form.get(field.field);
		if (control) {
			control.setValue(suggestion.pretty_name || suggestion.venue_name);
			control.updateValueAndValidity();
		}

		// // Handle company_and_resource_set if specified in schema
		// if (field.company_and_resource_set && suggestion.company_id && suggestion.resource_id) {
		// 	this.formBuilder.handleCompanyAndResourceSet(
		// 		this.formId,
		// 		field,
		// 		suggestion.company_id,
		// 		suggestion.resource_id,
		// 		this.form
		// 	);
		// }

		// Emit the venue selection to parent component
		this.venueSet.emit({
			venue: suggestion,
			field: field.field,
			companyId: suggestion.company_id,
			resourceId: suggestion.resource_id
		});

		this.cdr.markForCheck();
	}

	/**
	 * Venues for a `resource_dropdown` field, A-Z, with the same `filter_by` rule as `resource_lookup`.
	 * The template calls this only while the field shows, so the first call loads the venues once.
	 */
	dropdownVenues(field: any): any[] {
		if (!field.venues && !field.venuesLoading && !field.venuesError) {
			this.loadVenues(field);
		}
		const filterValue = String((field.filter_by && this.form.get(field.filter_by)?.value) || '').toLowerCase().trim();
		return (field.venues || []).filter(
			(venue: any) => !filterValue || venue.venue_type?.toLowerCase().trim() === filterValue
		);
	}

	private async loadVenues(field: any): Promise<void> {
		field.venuesLoading = true;
		try {
			const venues = await this.formBuilder.getVenues(this.appConfig);
			field.venues = venues.sort((a, b) =>
				(a.pretty_name || a.venue_name).localeCompare(b.pretty_name || b.venue_name, 'en-GB', { sensitivity: 'base' })
			);
		} catch (error) {
			console.error('Venue load error:', error);
			field.venuesError = true;
		}
		field.venuesLoading = false;
		this.cdr.markForCheck();
	}

	selectDropdownVenue(event: Event, field: any): void {
		const value = (event.target as HTMLSelectElement).value;
		const venue = (field.venues || []).find((v: any) => (v.pretty_name || v.venue_name) === value);
		if (venue) {
			this.selectSuggestion(venue, field);
		} else {
			this.clearSearch(field);
		}
	}

}
